import { useState, useEffect } from 'react';
import { userAPI, authAPI } from '../../services/api';
import { getRoleDisplayName } from '../../utils/roleTranslations';
import { User, Lock, Shield, Settings as SettingsIcon } from 'lucide-react';

interface UserSettingsProps {
  user: any;
}

export const UserSettings = ({ user }: UserSettingsProps) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | '2fa'>('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [twoFactorState, setTwoFactorState] = useState({
    enabled: user.twoFactorEnabled || false,
    qrCode: '',
    secret: '',
    verificationCode: '',
    showSetup: false,
    setupStep: 'initial' as 'initial' | 'setup' | 'verify',
    backupCodes: [] as string[],
    showBackupCodes: false,
    backupCodeStatus: { hasBackupCodes: false, remainingCodes: 0 },
    regenerateCode: '', // Separates Feld für Backup-Code-Generierung
    disableCode: ''     // Separates Feld für 2FA-Deaktivierung
  });

  // Load backup code status when 2FA is enabled
  useEffect(() => {
    if (twoFactorState.enabled) {
      console.log('🔄 Loading backup code status...');
      loadBackupCodeStatus();
    }
  }, [twoFactorState.enabled]);

  const loadBackupCodeStatus = async () => {
    try {
      console.log('📡 Fetching backup code status from API...');
      const response = await authAPI.getBackupCodeStatus();
      console.log('✅ Backup code status received:', response.data);
      setTwoFactorState(prev => ({
        ...prev,
        backupCodeStatus: response.data
      }));
    } catch (error: any) {
      console.error('❌ Error loading backup code status:', error);
      // Falls Fehler, setze Status auf 0
      setTwoFactorState(prev => ({
        ...prev,
        backupCodeStatus: { hasBackupCodes: false, remainingCodes: 0 }
      }));
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    console.log('🔄 Frontend: Starting password change');
    console.log('📝 Password data:', {
      hasCurrent: !!passwordData.currentPassword,
      hasNew: !!passwordData.newPassword,
      hasConfirm: !!passwordData.confirmPassword,
      currentLength: passwordData.currentPassword?.length,
      newLength: passwordData.newPassword?.length
    });

    if (!passwordData.currentPassword) {
      setMessage({ type: 'error', text: 'Aktuelles Passwort erforderlich' });
      setLoading(false);
      return;
    }

    if (!passwordData.newPassword) {
      setMessage({ type: 'error', text: 'Neues Passwort erforderlich' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Neue Passwörter stimmen nicht überein' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Passwort muss mindestens 6 Zeichen lang sein' });
      setLoading(false);
      return;
    }

    try {
      console.log('📤 Sending password update request...');
      
      const requestData = {
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword
      };
      
      console.log('📦 Request data prepared:', {
        hasCurrent: !!requestData.currentPassword,
        hasNew: !!requestData.password
      });

      const response = await userAPI.updateProfile(user.id, requestData);

      console.log('✅ Password update successful:', response.data);
      setMessage({ type: 'success', text: 'Passwort erfolgreich geändert' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
    } catch (error: any) {
      console.error('❌ Password update failed:', error);
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Fehler beim Ändern des Passworts';
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await authAPI.setup2FA();
      console.log('2FA Setup response:', response.data); // Debug log
      setTwoFactorState(prev => ({
        ...prev,
        qrCode: response.data.qrCode,
        secret: response.data.secret,
        backupCodes: response.data.backupCodes || [],
        showSetup: true,
        setupStep: 'setup'
      }));
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Setup von 2FA' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!twoFactorState.verificationCode || twoFactorState.verificationCode.length !== 6) {
      setMessage({ type: 'error', text: 'Bitte geben Sie einen 6-stelligen Code ein' });
      return;
    }

    if (!twoFactorState.secret || !twoFactorState.backupCodes.length) {
      setMessage({ type: 'error', text: 'Setup-Daten fehlen. Bitte starten Sie das Setup erneut.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const requestData = {
        token: twoFactorState.verificationCode,
        secret: twoFactorState.secret,
        backupCodes: twoFactorState.backupCodes
      };

      console.log('🔐 Sending 2FA enable request:', {
        hasToken: !!requestData.token,
        hasSecret: !!requestData.secret,
        backupCodesCount: requestData.backupCodes.length
      });

      await authAPI.enable2FA(requestData);
      
      setTwoFactorState(prev => ({
        ...prev,
        enabled: true,
        showSetup: false,
        setupStep: 'initial',
        verificationCode: '',
        qrCode: '',
        secret: '',
        backupCodes: []
      }));
      setMessage({ type: 'success', text: '2FA erfolgreich aktiviert' });
      
      user.twoFactorEnabled = true;
      
      // Backup-Code-Status nach Aktivierung laden
      setTimeout(() => loadBackupCodeStatus(), 1000);
      
    } catch (error: any) {
      console.error('❌ 2FA Enable Error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Ungültiger 2FA-Code' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!twoFactorState.disableCode || (twoFactorState.disableCode.length !== 6 && twoFactorState.disableCode.length !== 8)) {
      setMessage({ type: 'error', text: 'Bitte geben Sie einen 6-stelligen TOTP-Code oder 8-stelligen Backup-Code ein' });
      return;
    }

    if (!confirm('2FA wirklich deaktivieren? Dies reduziert die Sicherheit Ihres Kontos.')) {
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      await authAPI.disable2FA(twoFactorState.disableCode);
      setTwoFactorState(prev => ({
        ...prev,
        enabled: false,
        disableCode: ''
      }));
      setMessage({ type: 'success', text: '2FA deaktiviert' });
      
      user.twoFactorEnabled = false;
      
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Deaktivieren von 2FA' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!twoFactorState.regenerateCode || twoFactorState.regenerateCode.length !== 6) {
      setMessage({ type: 'error', text: 'Bitte geben Sie einen 6-stelligen TOTP-Code ein' });
      return;
    }

    if (!confirm('Neue Backup-Codes generieren? Alle alten Codes werden ungültig!')) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await authAPI.regenerateBackupCodes(twoFactorState.regenerateCode);
      setTwoFactorState(prev => ({
        ...prev,
        backupCodes: response.data.backupCodes,
        showBackupCodes: true,
        regenerateCode: ''
      }));
      setMessage({ type: 'success', text: 'Neue Backup-Codes erfolgreich generiert' });
      
      // Backup-Code-Status nach Regenerierung aktualisieren
      setTimeout(() => loadBackupCodeStatus(), 500);
      
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Fehler beim Generieren neuer Backup-Codes' 
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: <User size={16} /> },
    { id: 'password', label: 'Passwort', icon: <Lock size={16} /> },
    { id: '2fa', label: 'Zwei-Faktor', icon: <Shield size={16} /> }
  ];

  return (
    <div className="user-settings">
      <h2>
        <SettingsIcon size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Benutzersteuerung
      </h2>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id as any)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      <div className="settings-content">
        {activeTab === 'profile' && (
          <div className="settings-section">
            <h3>Profil-Informationen</h3>
            <div className="profile-info">
              <div className="info-group">
                <label>Name:</label>
                <span>{user.firstName} {user.lastName}</span>
              </div>
              <div className="info-group">
                <label>E-Mail:</label>
                <span>{user.email}</span>
              </div>
              <div className="info-group">
                <label>Rolle:</label>
                <span className="role-badge">{getRoleDisplayName(user.role)}</span>
              </div>
              <div className="info-group">
                <label>Mitglied seit:</label>
                <span>{new Date(user.createdAt || Date.now()).toLocaleDateString('de-DE')}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'password' && (
          <div className="settings-section">
            <h3>Passwort ändern</h3>
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Aktuelles Passwort:</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({...prev, currentPassword: e.target.value}))}
                  placeholder="Aktuelles Passwort eingeben"
                  required
                />
              </div>
              <div className="form-group">
                <label>Neues Passwort:</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e.target.value}))}
                  placeholder="Neues Passwort eingeben"
                  minLength={6}
                  required
                />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen:</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e.target.value}))}
                  placeholder="Neues Passwort wiederholen"
                  required
                />
              </div>
              <button type="submit" className="btn primary" disabled={loading}>
                {loading ? 'Ändern...' : 'Passwort ändern'}
              </button>
            </form>
          </div>
        )}

        {activeTab === '2fa' && (
          <div className="settings-section">
            <h3>Zwei-Faktor-Authentifizierung</h3>
            
            <div className="twofa-status">
              <div className="status-indicator-row">
                <span className={`status-dot ${twoFactorState.enabled ? 'enabled' : 'disabled'}`}></span>
                <span>
                  2FA ist <strong>{twoFactorState.enabled ? 'aktiviert' : 'deaktiviert'}</strong>
                </span>
              </div>
              
              {twoFactorState.enabled && (
                <div className="backup-codes-status">
                  <p>
                    <strong>Backup-Codes:</strong> {twoFactorState.backupCodeStatus.remainingCodes} verbleibend
                  </p>
                  {twoFactorState.backupCodeStatus.remainingCodes < 3 && (
                    <div className="warning-box">
                      <p>⚠️ Wenige Backup-Codes übrig! Generieren Sie neue Codes.</p>
                    </div>
                  )}
                </div>
              )}

              {!twoFactorState.enabled && !twoFactorState.showSetup && (
                <div className="twofa-setup-initial">
                  <button 
                    className="btn primary" 
                    onClick={handleSetup2FA} 
                    disabled={loading}
                  >
                    {loading ? 'Einrichten...' : (
                      <>
                        <Shield size={16} style={{ marginRight: '4px' }} />
                        2FA einrichten
                      </>
                    )}
                  </button>
                </div>
              )}

              {twoFactorState.showSetup && twoFactorState.setupStep === 'setup' && (
                <div className="twofa-setup">
                  <h4>🔐 Zwei-Faktor-Authentifizierung einrichten</h4>
                  
                  {twoFactorState.backupCodes.length > 0 && (
                    <div className="backup-codes-initial">
                      <h5>🔑 Backup-Codes (Nur einmal angezeigt!)</h5>
                      <div className="warning-box">
                        <p><strong>⚠️ WICHTIG:</strong> Speichern Sie diese Codes sicher! Sie werden nur einmal angezeigt und können verwendet werden, wenn Ihr Authenticator-Gerät nicht verfügbar ist.</p>
                      </div>
                      <div className="backup-codes-grid">
                        {twoFactorState.backupCodes.map((code, index) => (
                          <div key={index} className="backup-code-item">
                            <code className="backup-code">{code}</code>
                          </div>
                        ))}
                      </div>
                      <div className="backup-codes-actions">
                        <button 
                          type="button"
                          className="btn secondary"
                          onClick={() => {
                            const codesText = twoFactorState.backupCodes.join('\n');
                            navigator.clipboard.writeText(codesText);
                            setMessage({ type: 'success', text: 'Backup-Codes in Zwischenablage kopiert' });
                          }}
                        >
                          📋 Alle Codes kopieren
                        </button>
                        <button 
                          type="button"
                          className="btn secondary"
                          onClick={() => {
                            const element = document.createElement('a');
                            const file = new Blob([twoFactorState.backupCodes.join('\n')], {type: 'text/plain'});
                            element.href = URL.createObjectURL(file);
                            element.download = `chronios-backup-codes-${new Date().toISOString().split('T')[0]}.txt`;
                            document.body.appendChild(element);
                            element.click();
                            document.body.removeChild(element);
                          }}
                        >
                          💾 Als Datei speichern
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="setup-steps">
                    <div className="step">
                      <h5>📱 Schritt 1: Authenticator-App öffnen</h5>
                      <p>Verwenden Sie eine der folgenden Apps:</p>
                      <ul className="app-list">
                        <li>• Google Authenticator</li>
                        <li>• Microsoft Authenticator</li>
                        <li>• Ente Auth (empfohlen)</li>
                        <li>• Authy</li>
                        <li>• 1Password</li>
                      </ul>
                    </div>

                    <div className="step">
                      <h5>📷 Schritt 2: QR-Code scannen</h5>
                      <div className="qr-code-container">
                        <img 
                          src={twoFactorState.qrCode} 
                          alt="2FA QR Code für Chronios TMS" 
                          className="qr-code"
                        />
                      </div>
                    </div>
                    
                    <div className="step alternative">
                      <h5>⌨️ Alternative: Manuelle Eingabe</h5>
                      <div className="manual-setup-data">
                        <div className="data-row">
                          <label>Geheimer Schlüssel:</label>
                          <code className="secret-code">{twoFactorState.secret}</code>
                          <button 
                            type="button"
                            className="copy-btn"
                            onClick={() => navigator.clipboard.writeText(twoFactorState.secret)}
                          >
                            📋 Kopieren
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="step">
                      <h5>🔢 Schritt 3: Code eingeben</h5>
                      <div className="form-group">
                        <input
                          type="text"
                          value={twoFactorState.verificationCode}
                          onChange={(e) => setTwoFactorState(prev => ({
                            ...prev, 
                            verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                          }))}
                          placeholder="123456"
                          maxLength={6}
                          className="verification-input large"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="setup-buttons">
                    <button 
                      className="btn primary large" 
                      onClick={handleEnable2FA} 
                      disabled={loading || twoFactorState.verificationCode.length !== 6}
                    >
                      {loading ? '🔄 Aktivieren...' : '✅ 2FA aktivieren'}
                    </button>
                    <button 
                      className="btn secondary" 
                      onClick={() => setTwoFactorState(prev => ({
                        ...prev,
                        showSetup: false,
                        setupStep: 'initial',
                        qrCode: '',
                        secret: '',
                        verificationCode: '',
                        backupCodes: []
                      }))}
                      disabled={loading}
                    >
                      ❌ Abbrechen
                    </button>
                  </div>
                </div>
              )}

              {twoFactorState.enabled && (
                <div className="twofa-management">
                  <h4>2FA Verwaltung</h4>
                  
                  <div className="backup-codes-management">
                    <h5>Backup-Codes verwalten</h5>
                    <p>
                      Sie haben <strong>{twoFactorState.backupCodeStatus.remainingCodes}</strong> Backup-Codes verbleibend.
                      Diese können verwendet werden, wenn Ihr Authenticator-Gerät nicht verfügbar ist.
                    </p>
                    
                    <div className="form-group">
                      <label>6-stelliger TOTP-Code für neue Backup-Codes:</label>
                      <input
                        type="text"
                        value={twoFactorState.regenerateCode}
                        onChange={(e) => setTwoFactorState(prev => ({
                          ...prev, 
                          regenerateCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                        }))}
                        placeholder="123456"
                        maxLength={6}
                        className="verification-input"
                      />
                      <small>Verwenden Sie einen aktuellen 6-stelligen Code aus Ihrer Authenticator-App</small>
                    </div>
                    
                    <button 
                      className="btn warning" 
                      onClick={handleRegenerateBackupCodes}
                      disabled={loading || twoFactorState.regenerateCode.length !== 6}
                    >
                      {loading ? 'Generieren...' : '🔄 Neue Backup-Codes generieren'}
                    </button>

                    {twoFactorState.showBackupCodes && twoFactorState.backupCodes.length > 0 && (
                      <div className="new-backup-codes">
                        <h6>Neue Backup-Codes:</h6>
                        <div className="warning-box">
                          <p><strong>⚠️ WICHTIG:</strong> Speichern Sie diese neuen Codes sicher! Die alten Codes sind jetzt ungültig.</p>
                        </div>
                        <div className="backup-codes-grid">
                          {twoFactorState.backupCodes.map((code, index) => (
                            <div key={index} className="backup-code-item">
                              <code className="backup-code">{code}</code>
                            </div>
                          ))}
                        </div>
                        <div className="backup-codes-actions">
                          <button 
                            type="button"
                            className="btn secondary"
                            onClick={() => {
                              const codesText = twoFactorState.backupCodes.join('\n');
                              navigator.clipboard.writeText(codesText);
                              setMessage({ type: 'success', text: 'Backup-Codes in Zwischenablage kopiert' });
                            }}
                          >
                            📋 Alle Codes kopieren
                          </button>
                          <button 
                            type="button"
                            className="btn secondary"
                            onClick={() => {
                              const element = document.createElement('a');
                              const file = new Blob([twoFactorState.backupCodes.join('\n')], {type: 'text/plain'});
                              element.href = URL.createObjectURL(file);
                              element.download = `chronios-backup-codes-neu-${new Date().toISOString().split('T')[0]}.txt`;
                              document.body.appendChild(element);
                              element.click();
                              document.body.removeChild(element);
                            }}
                          >
                            💾 Als Datei speichern
                          </button>
                          <button 
                            type="button"
                            className="btn secondary"
                            onClick={() => setTwoFactorState(prev => ({ ...prev, showBackupCodes: false }))}
                          >
                            ❌ Ausblenden
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="twofa-disable">
                    
                    
                    <h4>2FA deaktivieren</h4>
                    <div className="warning-box">
                      <p>⚠️ <strong>Achtung:</strong> Das Deaktivieren von 2FA reduziert die Sicherheit Ihres Kontos.</p>
                    </div>
                    <p>Geben Sie einen aktuellen TOTP-Code (6 Stellen) oder einen Backup-Code (8 Stellen) ein:</p>
                    
                    <div className="form-group">
                      <label>Code für 2FA-Deaktivierung:</label>
                      <input
                        type="text"
                        value={twoFactorState.disableCode}
                        onChange={(e) => setTwoFactorState(prev => ({
                          ...prev, 
                          disableCode: e.target.value.replace(/\D/g, '').slice(0, 8)
                        }))}
                        placeholder="123456.."
                        maxLength={8}
                        className="verification-input"
                      />
                      <small>6-stelliger TOTP-Code aus Ihrer App oder 8-stelliger Backup-Code</small>
                    </div>
                    
                    <button 
                      className="btn danger" 
                      onClick={handleDisable2FA} 
                      disabled={loading || (twoFactorState.disableCode.length !== 6 && twoFactorState.disableCode.length !== 8)}
                    >
                      {loading ? 'Deaktivieren...' : '🚫 2FA deaktivieren'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};